import { cn } from '@client/utils';
import type { ReactNode } from 'react';

export type Tone = 'ok' | 'warning' | 'danger' | 'neutral' | 'info';

/** Border, fill and AA text classes for each tone, on the semantic status triplets. */
export const toneClasses = {
    ok: 'border-success-border bg-success-subtle text-success-foreground',
    warning: 'border-warning-border bg-warning-subtle text-warning-foreground',
    danger: 'border-danger-border bg-danger-subtle text-danger-foreground',
    info: 'border-info-border bg-info-subtle text-info-foreground',
    neutral: 'border-separator bg-muted text-muted-foreground',
} as const satisfies Record<Tone, string>;

type TonePillProps = {
    tone: Tone;
    children: ReactNode;
    /** `pill` is fully rounded with a leading dot (a summary state); `chip` is a squarer label. */
    shape?: 'pill' | 'chip';
    size?: 'small' | 'middle';
    /** Replaces the leading dot of a `pill`, or leads a `chip`. */
    icon?: ReactNode;
    className?: string;
};

/**
 * A small tone-coloured label for a summary state or classification that is not a workflow status
 * (workflow states use `StatusBadge`). The tone is a role, so the same colour means the same thing
 * wherever it is drawn.
 */
export function TonePill({ tone, children, shape = 'pill', size = 'small', icon, className }: TonePillProps) {
    const leading =
        icon ?? (shape === 'pill' ? <span aria-hidden className="size-1.5 rounded-full bg-current" /> : null);

    return (
        <span
            className={cn(
                'inline-flex items-center gap-1.5 whitespace-nowrap border font-semibold',
                shape === 'pill' ? 'rounded-full px-2 py-0.5' : 'rounded px-2 py-px',
                size === 'middle' ? 'text-sm' : 'text-xs',
                toneClasses[tone],
                className,
            )}
        >
            {leading}
            {children}
        </span>
    );
}
